import { CommandMan, type Humidifier } from "@eisox/api/core-api";
import { ActionButton, Modal } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { DropletIcon, PencilIcon } from "@eisox/icons";
import { getDirtyValues } from "@eisox/tools";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { Mode } from "~/components/live-history-toggle";
import { m } from "~/paraglide/messages";

import { useUpdateAirHandlerUnit } from "../hooks/use-update-air-handler-unit";
import { useAirHandlerUnitContext } from "../providers/air-handler-unit.provider";
import * as ActuatorDialog from "./actuator-dialog";

const NAME = "HumidifierDialog";

interface CommandFormValues {
  id: string;
  command: number | undefined;
  forcing: number | undefined;
}

interface HumidifierDialogProps extends Modal.RootProps {
  buildingId: string;
  airHandlerUnitId: string;
  humidifier: Humidifier;
  dehumidifier?: boolean;
  children?: React.ReactNode;
}

const HumidifierDialog: React.FC<HumidifierDialogProps> = ({
  buildingId,
  airHandlerUnitId,
  humidifier,
  dehumidifier = false,
  open: openProp,
  onOpenChange,
  defaultOpen,
  children,
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });

  const { mode } = useAirHandlerUnitContext(NAME);
  const disabled = mode === Mode.History;

  const { mutateAsync } = useUpdateAirHandlerUnit(buildingId);

  const title = dehumidifier ? m.air_handler_unit_dehumidifier() : m.air_handler_unit_humidifier();

  const defaultValues: CommandFormValues = {
    id: humidifier.id,
    command: humidifier.command,
    forcing: humidifier.forcing,
  };
  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value, formApi }) => {
      if (disabled) return;
      const dirty = getDirtyValues(formApi.options.defaultValues, value, { identity: "id" });
      if (!dirty) return;
      await mutateAsync({
        airHandlerUnitId,
        body: dehumidifier ? { dehumidifier: dirty } : { humidifier: dirty },
      });
      setOpen(false);
    },
  });

  // The form outlives the modal content, so reopening must restore the actuator's current values.
  const handleOpenChange = (next: boolean) => {
    if (next) form.reset();
    setOpen(next);
  };

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange}>
      {children && <Modal.Trigger asChild>{children}</Modal.Trigger>}
      <Modal.Content>
        <form
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <Modal.Header title={title} subtitle={m.air_handler_unit_actuator_dialog_subtitle()} icon={<DropletIcon />}>
              <Modal.Close asChild>
                <ActionButton variant="cancel">{m.cancel()}</ActionButton>
              </Modal.Close>
              {!disabled && <form.SubmitButton label={m.save()} icon={<PencilIcon />} />}
            </Modal.Header>
            <ActuatorDialog.Group label={title}>
              {(humidifier.command !== undefined || humidifier.commandMan === CommandMan.MANUAL) && (
                <form.AppField name="command">
                  {commandField => (
                    <ActuatorDialog.Item label={m.control()}>
                      <ActuatorDialog.Command
                        commandMan={humidifier.commandMan}
                        value={commandField.state.value}
                        onValueChange={value => commandField.handleChange(value)}
                        disabled={disabled}
                      />
                      <form.AppField name="forcing">
                        {forcingField => (
                          <ActuatorDialog.Forcing
                            command={commandField.state.value}
                            commandMan={humidifier.commandMan}
                            commandType={humidifier.commandType}
                            value={forcingField.state.value}
                            onValueChange={value => forcingField.handleChange(value)}
                            disabled={disabled}
                          />
                        )}
                      </form.AppField>
                    </ActuatorDialog.Item>
                  )}
                </form.AppField>
              )}
              <ActuatorDialog.Item label={m.state()}>
                <ActuatorDialog.ActuatorState
                  commandType={humidifier.commandType}
                  state={humidifier.state}
                  commandAutomate={humidifier.commandAutomate}
                  commandMan={humidifier.commandMan}
                  defect={!!humidifier.defect}
                  missmatchDefect={!!humidifier.missmatchDefect}
                />
              </ActuatorDialog.Item>
              {humidifier.operationHours !== undefined && (
                <ActuatorDialog.Item label={m.air_handler_unit_actuator_operating_hours()}>
                  <ActuatorDialog.State value={`${humidifier.operationHours}h`} />
                </ActuatorDialog.Item>
              )}
            </ActuatorDialog.Group>
          </form.AppForm>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
};

HumidifierDialog.displayName = NAME;

export { HumidifierDialog };
export type { HumidifierDialogProps };
